/** @format */

import { useEffect, type RefObject } from 'react';

/**
 * Lock background scrolling while a cell popover is open. Wheel / touch scroll
 * that originates OUTSIDE the popover's own content is cancelled, so the table
 * (and page) stay put and the popover remains anchored to its trigger instead
 * of drifting or closing. Scrolls inside `contentRef` — the popover's own
 * option / slot / thread list — still work normally.
 *
 * Listeners are capturing + non-passive so the background scroll can actually be
 * preventDefault-ed.
 */
export function useLockScrollWhileOpen(
  open: boolean,
  contentRef?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    const prevent = (e: Event) => {
      const content = contentRef?.current;
      // Allow scrolling within the popover's own content.
      if (content && e.target instanceof Node && content.contains(e.target)) return;
      e.preventDefault();
    };
    const opts = { passive: false, capture: true } as const;
    document.addEventListener('wheel', prevent, opts);
    document.addEventListener('touchmove', prevent, opts);
    return () => {
      document.removeEventListener('wheel', prevent, true);
      document.removeEventListener('touchmove', prevent, true);
    };
  }, [open, contentRef]);
}
